import { AbilityBuilder, Ability } from '@casl/ability';
import Vue from 'vue';

import PurchaseOrderLineStatus from '@/models/purchaseOrderLineStatus';
import RoleType from '@/models/roleType';

function applyPoManagmentRules(can) {
  can('view', 'AssignedPurchaseOrders');
}

const commentStatuses = [
  PurchaseOrderLineStatus.UnderReview.key,
  PurchaseOrderLineStatus.Approved.key,
  PurchaseOrderLineStatus.ImagesUploaded.key,
  PurchaseOrderLineStatus.NotSubmitted.key,
  PurchaseOrderLineStatus.NotApproved.key,
];

const rejectStatuses = [
  PurchaseOrderLineStatus.UnderReview.key,
  PurchaseOrderLineStatus.Approved.key,
];

export const buildRules = function buildRules(userId, roles, clients) {
  const { can, rules } = new AbilityBuilder(Ability);
  can('read', 'PO');

  roles.forEach((role) => {
    switch (role) {
      case RoleType.Supplier.key:
        can('view', 'Search');
        can('upload', 'LineItemImage', {
          status: {
            $in: [
              PurchaseOrderLineStatus.NotSubmitted.key,
              PurchaseOrderLineStatus.ImagesUploaded.key,
              PurchaseOrderLineStatus.NotApproved.key,
            ],
          },
        });
        can('remove', 'LineItemImage', {
          status: {
            $in: [
              PurchaseOrderLineStatus.ImagesUploaded.key,
            ],
          },
          userId,
        });

        can('view', 'SubmitAction');
        can('view', 'DownloadAction');
        can('view', 'Instructions');

        can('submit', 'LineItem', {
          status: {
            $in: [
              PurchaseOrderLineStatus.ImagesUploaded.key,
            ],
          },
        });

        can('download', 'LineItem', {
          status: PurchaseOrderLineStatus.Approved.key,
        });

        can('view', 'SelectManyItems');

        can('comment', 'LineItem', {
          status: {
            $in: commentStatuses,
          },
        });
        break;
      case RoleType.Merchandiser.key:
        can('view', 'Search');
        can('view', 'Instructions');
        can('view', 'ApproveAction', {
          clients: {
            $in: clients,
          },
        });
        can('view', 'RejectAction', {
          clients: {
            $in: clients,
          },
        });
        can('view', 'SelectManyItems', {
          clients: {
            $in: clients,
          },
        });
        can('approve', 'LineItem', {
          status: PurchaseOrderLineStatus.UnderReview.key,
          client: {
            $in: clients,
          },
        });
        can('reject', 'LineItem', {
          status: {
            $in: rejectStatuses,
          },
          client: {
            $in: clients,
          },
        });
        can('comment', 'LineItem', {
          status: {
            $in: commentStatuses,
          },
          client: {
            $in: clients,
          },
        });
        applyPoManagmentRules(can);
        break;
      case RoleType.WarehouseEmployee.key:
        can('view', 'Search');
        can('view', 'Instructions');
        can('view', 'ReportIssue');
        can('report', 'LineItem', {
          status: PurchaseOrderLineStatus.Approved.key,
        });
        can('view', 'SelectManyItems');
        can('comment', 'LineItem', {
          status: {
            $in: commentStatuses,
          },
        });
        break;
      case RoleType.Manager.key:
        can('view', 'Search');
        can('view', 'ApproveAction', {
          isMyClient: true,
        });
        can('view', 'ApproveAction', {
          isMyEmployeeClient: true,
        });
        can('approve', 'LineItem', {
          status: PurchaseOrderLineStatus.UnderReview.key,
          isMyClient: true,
        });
        can('approve', 'LineItem', {
          status: PurchaseOrderLineStatus.UnderReview.key,
          isMyEmployeeClient: true,
        });
        can('view', 'SubordinateWorkload');
        can('view', 'SelectManyItems', {
          isMyClient: true,
        });
        can('view', 'SelectManyItems', {
          isMyEmployeeClient: true,
        });
        can('view', 'UsersManagement');
        can('view', 'EmployeeManagement');
        can(['edit', 'read'], 'Employee.Clients');
        can('read', 'Employee.Name');
        can('read', 'Employee.Email');
        can('read', 'Employee.Role');
        can('edit', 'Employee');
        can('comment', 'LineItem', {
          status: {
            $in: commentStatuses,
          },
          isMyClient: true,
        });
        applyPoManagmentRules(can);
        break;
      case RoleType.Admin.key:
        can('view', 'Search');
        can('view', 'ApproveAction');
        can('approve', 'LineItem', {
          status: PurchaseOrderLineStatus.UnderReview.key,
        });
        can('view', 'SelectManyItems');
        can('reject', 'LineItem', {
          status: {
            $in: rejectStatuses,
          },
        });
        can('view', 'RejectAction');
        can('view', 'Search');
        can('view', 'UsersManagement');
        can('view', 'SuppliersManagement');
        can('view', 'EmployeeManagement');
        can('deleteComment', 'Event');
        can('resend', 'EmailVerification', {
          isEmailConfirmed: false,
        });

        can(['edit', 'read'], 'Employee.Clients');
        can(['edit', 'read'], 'Employee.Name');
        can(['edit', 'read'], 'Employee.Email');
        can(['edit', 'read'], 'Employee.Role');
        can(['edit', 'read'], 'Employee.Manager');
        can(['edit', 'read'], 'Employee.Enabled');
        can(['add', 'edit'], 'Employee');
        break;
      case RoleType.RolesManager.key:
        can('view', 'UsersManagement');
        can('view', 'SuppliersManagement');
        can('view', 'EmployeeManagement');
        can('resend', 'EmailVerification', {
          isEmailConfirmed: false,
        });

        can(['edit', 'read'], 'Employee.Clients');
        can(['edit', 'read'], 'Employee.Name');
        can(['edit', 'read'], 'Employee.Email');
        can(['edit', 'read'], 'Employee.Role');
        can(['edit', 'read'], 'Employee.Manager');
        can(['edit', 'read'], 'Employee.Enabled');
        can(['add', 'edit'], 'Employee');
        break;

      default:
        can('read', 'Login');
        break;
    }
  });

  return rules;
};

export const extendCaslCanMixin = function extendCaslCanMixin() {
  Vue.mixin({
    methods: {
      $isAllowed(action, subject, object) {
        if (!object) {
          this.$can(action, subject);
        }

        return this.$can(action, {
          ...object,
          __type: subject,
        });
      },
    },
  });
};
